import React, { useState, useEffect, useRef } from 'react';
import { Switch } from "@Components/shadcn/ui/switch";
import TriggerStep from './components/TriggerStep';
import ResponseStep from './components/ResponseStep';
import { useGetFlowAutomationMutation, useUpdateFlowAutomationMutation } from '@Pages/AutomationBuilder/apiSlice';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { displayToast } from '@Utils/toast';
import { useUpdateAutomationStatusMutation, useCreateAutomationMutation } from '@Pages/AutomationList/apiSlice';
import { AnalyticsParamBuilder, useUpgradeContext, validateUrl } from '@Utils/common';
import { RootState, useAppSelector } from '@Store/store';
import { PaperAirplaneIcon } from '@heroicons/react/24/solid';
import { Tooltip } from 'react-tooltip';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useRerunTriggerMutation } from './apiSlice';
import posthog from 'posthog-js';
import ResponseStepV2 from './components/ResponseStepV2';
import { ROUTES } from '@Constants/routes';

interface AutomationBuilderProps {
  automationId?: string;
}
const COMPONENT = "automation_builder"
const AutomationBuilderV0: React.FC<AutomationBuilderProps> = () => {
  const [getFlowAutomation] = useGetFlowAutomationMutation();
  const [updateAutomationStatus, { isLoading: isStatusUpdating }] = useUpdateAutomationStatusMutation()
  const [updateFlowAutomation, { isLoading: isUpdating }] = useUpdateFlowAutomationMutation();
  const [createAutomation, { isLoading: isCreating }] = useCreateAutomationMutation();

  const [reRunAutomation, { data: reRunAutomationData, error: reRunAutomationError, isLoading: reRunAutomationIsLoading, }] = useRerunTriggerMutation();

  const isGlobalLoading = isStatusUpdating || isUpdating || isCreating || reRunAutomationIsLoading;

  let { automationId: automation_id } = useParams();

  const automationId = Number(automation_id)

  const { user } = useAppSelector((state: RootState) => state.profile);

  const selectedIgData = useAppSelector((state: RootState) => state.igSelection.selectedIgData);
  const ig_user = selectedIgData

  const { setUpgradeToPro } = useUpgradeContext();

  const [LimitReached, setLimitReached] = useState(false)

  const navigate = useNavigate()
  const location = useLocation();

  const isSaving = isUpdating || isCreating;

  useEffect(() => {
    if (ig_user) {
      const automation_triggered = ig_user?.automation_triggered
      const ig_contacts = ig_user?.contacts

      if (!user?.is_pro && (automation_triggered > 1000 || ig_contacts > 1000)) {
        setLimitReached(true)
      }
    }
  }, [user])


  const [triggerData, setTriggerData] = useState<any>({});
  const [responseData, setResponseData] = useState<any>({
    opening_message: { enabled: false },
    data: []
  });

  const [automationName, setAutomationName] = useState('Untitled Automation');
  const [isNameEditing, setIsNameEditing] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);


  // Store the original data from API
  const originalData = useRef<{
    name: string;
    trigger: any;
    response: any;
  } | null>(null);


  const fetchAutomation = async () => {
    if (automationId) {
      try {
        const resp = await getFlowAutomation({ automation_id: automationId }).unwrap();
        if (resp) {
          if (resp?.data?.trigger) setTriggerData(resp?.data?.trigger || {});
          if (resp?.data?.response) setResponseData(resp?.data?.response);
          setAutomationName(resp?.data?.name || 'Untitled Automation');
          setIsEnabled(resp?.data?.status || false);
          // Store original data for comparison
          originalData.current = {
            name: resp.data.name,
            trigger: resp.data.trigger,
            response: resp.data.response
          };

        }
      } catch (error) {
        console.error('Failed to fetch automation:', error);
        displayToast("error", 'Failed to fetch automaton!');
        navigate('/dashboard/automations');
      }
    }
  };

  useEffect(() => {
    if (isNaN(automationId) && location.state?.clonedData) {
      const data = location.state.clonedData;
      setTriggerData(data.trigger || {});
      setResponseData(data.response || { opening_message: { enabled: false }, data: [] });
      setAutomationName(data.name ? `Copy of ${data.name}` : 'Untitled Automation');
      setIsEnabled(false);
      // Do not set originalData so dirty check/save works logic works? 
      // Or set it to initial cloned state?
      // Usually creation implies unsaved changes until saved.
      originalData.current = {
        name: data.name ? `Copy of ${data.name}` : 'Untitled Automation',
        trigger: data.trigger || {},
        response: data.response || { opening_message: { enabled: false }, data: [] }
      };
    } else {
      fetchAutomation();
    }
    document.title = "LinkPlease - Dashboard | Automation Edit";
  }, [automationId, location.state]);

  // useEffect(() => {
  //   if (triggerData?.triggerType == "USERDM") {
  //     let tempResponseData = {
  //       ...responseData
  //     }
  //     setResponseData(tempResponseData)
  //   }
  // }, [triggerData]);


  const hasUnsavedChanges = () => {
    if (!originalData.current) return false;
    return (
      JSON.stringify(originalData.current.name) !== JSON.stringify(automationName) ||
      JSON.stringify(originalData.current.trigger) !== JSON.stringify(triggerData) ||
      JSON.stringify(originalData.current.response) !== JSON.stringify(responseData)
    );
  };

  const CallUpdateStatusAPI = async (checked: boolean, error?: string) => {
    try {
      const response = await updateAutomationStatus({
        automation_id: automationId,
        status: checked // Pass the updated value to the API
      }).unwrap();
      if (response) {
        setIsEnabled(checked);

        if (error) {
          posthog.capture("ui_msg", AnalyticsParamBuilder({
            kingdom: COMPONENT,
            phylum: "switch_automation_on_off",
            classValue: checked,
            genus: "error",
            str_value: error,
            int_value: automationId
          }));
          displayToast("error", error);
        } else {
          posthog.capture("ui_msg", AnalyticsParamBuilder({
            kingdom: COMPONENT,
            phylum: "switch_automation_on_off",
            classValue: checked,
            genus: "success",
            int_value: automationId
          }));
          displayToast("success", response?.message || 'Status updated successfully');
        }

        fetchAutomation();
      }
    } catch (error) {
      console.error('Failed to update automation status:', error);
      displayToast("error", 'Failed to update automation status');
    }
  }

  const handleSwitchChange = async (checked: boolean) => {
    posthog.capture("ui_action", AnalyticsParamBuilder({
      kingdom: COMPONENT,
      phylum: "switch_automation_on_off",
      classValue: checked,
      int_value: automationId
    }));

    if (checked && hasUnsavedChanges()) {
      displayToast("warning", 'Please save your changes before enabling the automation.');
      posthog.capture("ui_msg", AnalyticsParamBuilder({
        kingdom: COMPONENT,
        phylum: "switch_automation_on_off",
        classValue: checked,
        genus: "warning",
        family: "unsaved_changes",
        int_value: automationId
      }));
      return;
    }

    if (checked && LimitReached) {
      displayToast("warning", 'Free tier exhausted! Upgrade to PRO plan to continue');
      posthog.capture("ui_toast", AnalyticsParamBuilder({
        kingdom: COMPONENT,
        phylum: "switch_automation_on_off",
        classValue: checked,
        genus: "warning",
        family: "limit_reached",
        int_value: automationId
      }));
      setUpgradeToPro(true)
      return;
    }
    const valid_trigger = ValidateTrigger()
    const responseValidation = ValidateResponse()
    const valid_response = responseValidation.valid

    // Update validation errors state
    setValidationErrors(responseValidation.errorMessages)

    if (!checked || (valid_trigger && valid_response)) {
      await CallUpdateStatusAPI(checked)
    } else {
      displayToast("warning", 'Cannot turn ON automation due to errors. Please fix the issues and save before activating.');
    };

  };

  const handleNameEdit = () => {
    posthog.capture("ui_action", AnalyticsParamBuilder({
      kingdom: COMPONENT,
      phylum: "name_change",
      classValue: "pencil_icon",
    }));
    setIsNameEditing(true);
  };

  const handleNameSave = (e: React.FormEvent) => {
    e.preventDefault();
    posthog.capture("ui_action", AnalyticsParamBuilder({
      kingdom: COMPONENT,
      phylum: "name_change",
      classValue: "tick_icon",
      str_value: automationName
    }));
    setIsNameEditing(false);
  };

  const handleSaveAutomation = async () => {
    try {
      posthog.capture("ui_action", AnalyticsParamBuilder({
        kingdom: COMPONENT,
        phylum: "save_automation",
        int_value: automationId
      }));

      const valid_trigger = ValidateTrigger()
      const responseValidation = ValidateResponse()
      const valid_response = responseValidation.valid

      // Update validation errors state
      setValidationErrors(responseValidation.errorMessages)

      // Show warnings for response validation issues but allow saving
      if (!valid_response) {
        displayToast("warning", "Automation saved with validation warnings. Please fix the issues.");
      }

      let currentAutomationId = automationId;

      if (isNaN(currentAutomationId)) {
        // Create new automation first
        if (!ig_user?.igsid) {
          displayToast("error", "Instagram Connection not found");
          return;
        }

        const createResp = await createAutomation({ ig_id: ig_user.igsid }).unwrap();
        if (createResp?.data?.automation_id) {
          currentAutomationId = createResp.data.automation_id;
          // Continue to update with data
        } else {
          throw new Error("Failed to create new automation");
        }
      }

      // Normalize AskForFollow postback_text and OpeningMessage postback_text with correct automationID
      const fixedResponseData = {
        ...responseData,
        opening_message: {
          ...responseData?.opening_message,
          buttonText: responseData?.opening_message?.buttonText?.map((btn: any) => ({
            ...btn,
            postback_text: `OpeningMessage#${user?.user_id}#${currentAutomationId}#-1`
          }))
        },
        data: (responseData?.data || [])
          .filter((comp: any) => comp?.type !== 'end_automation')
          .map((comp: any, idx: number) => {
            if (comp?.type === 'ask_follow') {
              const buttons = comp?.buttons || [];
              const newButtons = buttons.map((btn: any, bIdx: number) => {
                if (bIdx === 1) {
                  const updatedPb = `AskForFollow#${user?.user_id}#${currentAutomationId}#${idx}`;
                  return { ...btn, postback_text: updatedPb };
                }
                return btn;
              });
              return { ...comp, buttons: newButtons };
            }
            return comp;
          }),
      };

      const automationData = {
        automation_id: currentAutomationId,
        name: automationName,
        isEnabled,
        data: {
          trigger: triggerData,
          response: fixedResponseData
        }
      };

      const resp = await updateFlowAutomation(automationData).unwrap();

      if (resp) {
        posthog.capture("api_response", AnalyticsParamBuilder({
          kingdom: COMPONENT,
          phylum: "save_automation",
          classValue: "success",
          str_value: JSON.stringify(resp)
        }));
        displayToast("success", resp?.message);

        // Update local state with the fixed data containing correct IDs
        setResponseData(fixedResponseData);

        originalData.current = {
          name: automationName,
          trigger: triggerData,
          response: fixedResponseData
        };

        // If it was a new creation (NaN check was true)
        if (isNaN(automationId)) {
          navigate(`/dashboard/automations/${currentAutomationId}`, { replace: true });
        } else {
          // If automation is enabled but has validation issues, deactivate it
          if (isEnabled && (!valid_response || !valid_trigger)) {
            await CallUpdateStatusAPI(false, "Automation Paused due to validation errors");
          }
        }
      }
    } catch (error: any) {
      posthog.capture("catch_error", AnalyticsParamBuilder({
        kingdom: COMPONENT,
        phylum: "save_automation",
        classValue: "error",
        str_value: JSON.stringify(error)
      }));
      displayToast("error", error?.data?.message || 'Failed to update automation status');
    }
  };


  const ValidateTrigger = () => {
    let valid = true
    if (triggerData?.triggerType == "USERDM") {
      if (triggerData?.keywords.length == 0) {
        displayToast("error", "No Trigger Selected")
        valid = false
      }
    }

    if (triggerData?.triggerType == "POSTREELCOMMENT") {
      if (triggerData?.keywords == undefined || triggerData?.keywords.length == 0) {
        displayToast("error", "No Trigger Keyword Added")
        valid = false
      }
      if (triggerData?.selectedMedia == undefined) {
        displayToast("error", "No Trigger post/reel selected")
        valid = false
      }
      if (triggerData?.commentReplies == undefined) {
        displayToast("warning", "No comment reply added")
      }
    }

    if (triggerData?.triggerType == "LIVECOMMENT" || triggerData?.triggerType == "STORYCOMMENT") {
      if (triggerData?.keywords == undefined || triggerData?.keywords.length == 0) {
        displayToast("error", "No Trigger Keyword Added")
        valid = false
      }
      if (triggerData?.selectedMedia == undefined) {
        displayToast("error", "No Trigger post/reel selected")
        valid = false
      }
    }

    return valid
  }

  const ValidateResponse = () => {
    let valid = true;
    let errorMessages: string[] = [];

    // Check if response has any components
    const hasResponseComponents = responseData?.data && responseData.data.length > 0;
    const hasOpeningMessage = responseData?.opening_message?.enabled;

    // Case 1: When Trigger is post/reel comment or Live Comment and user has User Input or Ask for Follow components
    if ((triggerData?.triggerType === "POSTREELCOMMENT" || triggerData?.triggerType === "LIVECOMMENT") && hasResponseComponents) {
      const hasUserInput = responseData.data.some((item: any) => item.type === 'user_input');
      const hasAskForFollow = responseData.data.some((item: any) => item.type === 'ask_follow');

      if ((hasUserInput || hasAskForFollow) && !hasOpeningMessage) {
        errorMessages.push("Opening message is required when using Lead Forms or Ask for Follow");
        valid = false;
      }
    }

    // Case 2: Only opening message is not allowed (must have other components)
    if (hasOpeningMessage && !hasResponseComponents) {
      errorMessages.push("Response cannot contain only opening message. Please add other response components");
      valid = false;
    }

    // Case 2b: When opening message is OFF, allow only one of text or card total
    if (!hasOpeningMessage && hasResponseComponents) {
      const countText = responseData.data.filter((i: any) => i?.type === 'text_message').length
      const countCard = responseData.data.filter((i: any) => i?.type === 'card_message').length
      if (countText + countCard > 1) {
        errorMessages.push("When opening message is OFF, only one of Text or Card is allowed");
        valid = false;
      }
    }

    // Case 3: User Input components must have at least one active question and valid field values
    if (hasResponseComponents) {
      const userInputComponents = responseData.data.filter((item: any) => item.type === 'user_input');
      userInputComponents.forEach((userInput: any, index: number) => {
        const activeQuestions = userInput.questions?.filter((question: any) => question.enabled) || [];
        if (activeQuestions.length === 0) {
          errorMessages.push(`Lead Forms ${index + 1} must have at least one active question`);
          valid = false;
        }

        // Validate each active question has non-empty question and errorMessage
        activeQuestions.forEach((question: any, qIndex: number) => {
          if (!question.question || question.question.trim().length === 0) {
            errorMessages.push(`Lead Forms ${index + 1}, Question ${qIndex + 1} (${question.label}): Question text cannot be empty`);
            valid = false;
          }
          if (!question.errorMessage || question.errorMessage.trim().length === 0) {
            errorMessages.push(`Lead Forms ${index + 1}, Question ${qIndex + 1} (${question.label}): Error message cannot be empty`);
            valid = false;
          }
        });
      });
    }

    // Case 4: Only Ask for Follow component is not allowed
    if (hasResponseComponents && responseData.data.length === 1) {
      const onlyComponent = responseData.data[0] as any;
      if (onlyComponent?.type === 'ask_follow') {
        errorMessages.push("Response cannot contain only Ask for Follow component. Please add other response components");
        valid = false;
      }
    }

    // Case 5: Validate component values (text buttons, card buttons)
    if (hasResponseComponents) {
      responseData.data.forEach((comp: any, compIdx: number) => {
        if (comp?.type === 'text_message') {
          const buttons = comp?.buttonText || [];
          buttons.forEach((btn: any, idx: number) => {
            const label = `Text Message button ${idx + 1}`;
            if (!btn?.title || btn.title.trim().length === 0) {
              errorMessages.push(`${label}: text is required`);
              valid = false;
            } else if (btn.title.length > 20) {
              errorMessages.push(`${label}: text must be ≤ 20 characters`);
              valid = false;
            }
            if (btn?.type === 'link') {
              if (!btn?.data || btn.data.trim().length === 0) {
                errorMessages.push(`${label}: URL is required`);
                valid = false;
              } else if (!validateUrl(btn.data)) {
                errorMessages.push(`${label}: invalid URL`);
                valid = false;
              }
            }
          });
        }

        if (comp?.type === 'card_message') {
          const cards = comp?.data?.cards || [];
          cards.forEach((card: any, cIdx: number) => {
            const buttons = card?.buttons || [];
            buttons.forEach((btn: any, bIdx: number) => {
              const label = `Card ${cIdx + 1} button ${bIdx + 1}`;
              if (!btn?.title || btn.title.trim().length === 0) {
                errorMessages.push(`${label}: text is required`);
                valid = false;
              } else if (btn.title.length > 20) {
                errorMessages.push(`${label}: text must be ≤ 20 characters`);
                valid = false;
              }
              if (btn?.type === 'link') {
                if (!btn?.data || btn.data.trim().length === 0) {
                  errorMessages.push(`${label}: URL is required`);
                  valid = false;
                } else if (!validateUrl(btn.data)) {
                  errorMessages.push(`${label}: invalid URL`);
                  valid = false;
                }
              }
            });
          });
        }
      });
    }

    return { valid, errorMessages };
  }

  useEffect(() => {
    if (reRunAutomationData) {
      posthog.capture("api_response", AnalyticsParamBuilder({
        kingdom: COMPONENT,
        phylum: "re_run_automation",
        classValue: "success",
        str_value: JSON.stringify(reRunAutomationData)
      }));
      displayToast('success', reRunAutomationData?.message || "Retriggered Successfully!")
    }

    if (reRunAutomationError) {
      posthog.capture("api_response", AnalyticsParamBuilder({
        kingdom: COMPONENT,
        phylum: "re_run_automation",
        classValue: "error",
        str_value: JSON.stringify(reRunAutomationError)
      }));
      displayToast('error', reRunAutomationError?.data?.message || reRunAutomationError?.data?.message?.error || "Something went wrong")
    }
  }, [reRunAutomationData, reRunAutomationError])


  const OnReRunButtonClick = () => {
    posthog.capture("ui_action", AnalyticsParamBuilder({
      kingdom: COMPONENT,
      phylum: "re_run_automation",
      int_value: automationId
    }));
    if (!user?.is_pro) {
      posthog.capture("ui_msg", AnalyticsParamBuilder({
        kingdom: COMPONENT,
        phylum: "re_run_automation",
        family: "pro_feature",
        genus: "warning",
        int_value: automationId
      }));
      displayToast('warning', "This is a PRO Feature!")
      setUpgradeToPro(true);
      return
    };
    reRunAutomation({ automation_id: automationId })
  }

  return (
    <div className="min-h-screen bg-gray-50/50 w-full pb-20">
      {/* Top Navigation Bar */}
      <div className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-200/50 shadow-sm transition-all duration-200">
        <div className="max-w-7xl mx-auto px-4 py-3 md:h-16 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/dashboard/automations')}
              className="text-gray-500 hover:text-gray-900 transition-colors flex items-center gap-1 text-sm font-medium"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
              </svg>
              Back
            </button>
            <div className="h-4 w-px bg-gray-300 mx-1"></div>

            {isNameEditing ? (
              <form onSubmit={handleNameSave} className="flex items-center gap-2">
                <input
                  type="text"
                  value={automationName}
                  onChange={(e) => setAutomationName(e.target.value)}
                  className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 text-gray-900 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none w-[200px]"
                  autoFocus
                />
                <button type="submit" className="p-1.5 text-green-600 hover:bg-green-50 rounded-md transition-colors">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </button>
              </form>
            ) : (
              <div className="flex items-center gap-2 group cursor-pointer" onClick={handleNameEdit}>
                <span className="text-gray-900 font-semibold text-base truncate max-w-[200px] md:max-w-[300px]">{automationName}</span>
                <svg className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </div>
            )}
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-4">
            {/* Desktop Controls */}
            <div className="hidden md:flex items-center gap-4">
              {triggerData?.triggerType == "POSTREELCOMMENT" && (
                <div className='flex items-center gap-2'>
                  <Tooltip anchorSelect=".retrigger" style={{ backgroundColor: '#333', color: 'white', borderRadius: '8px', fontSize: '12px' }} place="bottom">
                    Re-trigger Automations on missed comments
                  </Tooltip>
                  <button
                    disabled={isGlobalLoading}
                    onClick={OnReRunButtonClick}
                    className='retrigger inline-flex items-center gap-1.5 px-3 py-1.5 border border-blue-200 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    <PaperAirplaneIcon className='size-4' />
                    <span>Re-Trigger</span>
                    {reRunAutomationIsLoading && <span className='loading_spinner size-3'></span>}
                  </button>
                </div>
              )}

              <div className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200 min-w-[100px] justify-between">
                <label className="text-sm font-medium text-gray-600">Status</label>
                {isStatusUpdating ? (
                  <div className="w-9 h-5 flex items-center justify-center">
                    <div className="w-3.5 h-3.5 border-2 border-gray-400 border-t-emerald-600 rounded-full animate-spin" />
                  </div>
                ) : (
                  <Switch
                    checked={isEnabled}
                    onCheckedChange={handleSwitchChange}
                    disabled={isGlobalLoading}
                    className="data-[state=checked]:bg-emerald-500 scale-90"
                  />
                )}
              </div>

              <button
                onClick={handleSaveAutomation}
                disabled={isGlobalLoading}
                className="px-4 py-2 bg-[#3535d5] hover:bg-[#2b2bb8] text-white rounded-xl font-medium transition-all shadow-sm hover:shadow hover:-translate-y-0.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed min-w-[120px]"
              >
                {isSaving ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Saving...</span>
                  </div>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>

            {/* Mobile Controls */}
            <div className="flex md:hidden flex-col gap-3 pb-2">
              <div className="flex items-center justify-between bg-gray-50 p-2 rounded-lg border border-gray-100">
                <span className="text-sm font-medium text-gray-600">Status</span>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-bold ${isEnabled ? 'text-emerald-600' : 'text-gray-400'}`}>
                    {isEnabled ? 'ACTIVE' : 'INACTIVE'}
                  </span>
                  {isStatusUpdating && (
                    <div className="w-9 h-5 flex items-center justify-center">
                      <div className="w-3.5 h-3.5 border-2 border-gray-400 border-t-emerald-600 rounded-full animate-spin" />
                    </div>
                  )}
                  <Switch
                    checked={isEnabled}
                    onCheckedChange={handleSwitchChange}
                    disabled={isGlobalLoading}
                    className={`data-[state=checked]:bg-emerald-500 scale-90 ${isStatusUpdating ? 'hidden' : ''}`}
                  />
                </div>
              </div>

              <div className="flex gap-2">
                {triggerData?.triggerType == "POSTREELCOMMENT" && (
                  <button
                    disabled={isGlobalLoading}
                    onClick={OnReRunButtonClick}
                    className='flex-1 flex items-center justify-center gap-1.5 px-3 py-2 border border-blue-200 text-blue-600 bg-blue-50 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    <PaperAirplaneIcon className='size-4' />
                    <span>{reRunAutomationIsLoading ? 'Re-Triggering...' : 'Re-Trigger'}</span>
                  </button>
                )}

                <button
                  onClick={handleSaveAutomation}
                  disabled={isGlobalLoading}
                  className="flex-1 px-4 py-2 bg-[#3535d5] text-white rounded-lg font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {triggerData && <TriggerStep data={triggerData} onUpdate={setTriggerData} automationId={automationId} />}

        {/* ResponseStepV2 Component */}
        <ResponseStepV2
          data={responseData}
          onUpdate={setResponseData}
          automationId={automationId}
          validationErrors={validationErrors}
        />
      </div>
    </div>
  );
};

export default AutomationBuilderV0;